if(!customElements.get("mini-cart")) {
class MiniCart extends HTMLElement {
  constructor() {
    super();
    this.header = document.querySelector('sticky-header');
    this.drawer = document.querySelector('cart-drawer');
    this.initialized = false; // Track if initial render has occurred
  }

  connectedCallback() {
    // Set up IntersectionObserver for lazy-loading mini-cart content
    new IntersectionObserver(this.handleIntersection.bind(this)).observe(this);

    // Ensure cart icon sections are rendered on page load
    document.addEventListener('DOMContentLoaded', () => {
      if (!this.initialized) {
        this.initialRenderCartIcons();
      }
    });

    // Fallback: If the page is already loaded, render immediately
    if (document.readyState === 'complete' && !this.initialized) {
      this.initialRenderCartIcons();
    }
  }

  async initialRenderCartIcons() {
    console.log('Initial rendering of cart icons...');
    try {
      const sectionsToRender = this.getSectionsToRender().filter(section => section.id !== 'mini-cart'); // Exclude mini-cart section
      const sectionIds = sectionsToRender.map(section => section.section).filter(Boolean).join(',');

      if (!sectionIds) return;

      const sectionResponse = await fetch(`?sections=${sectionIds}`);
      if (!sectionResponse.ok) throw new Error(`Failed to fetch initial cart sections: ${sectionResponse.status}`);
      const sectionData = await sectionResponse.json();

      sectionsToRender.forEach((section) => {
        const element = document.getElementById(section.id);
        if (!element) {
          console.warn(`Element with ID ${section.id} not found`);
          return;
        }
        const html = sectionData[section.section];
        if (html) {
          const innerHTML = this.getSectionInnerHTML(html, section.selector);
          element.innerHTML = innerHTML || '';
        } else {
          console.warn(`No section data for ${section.id} in initial fetch`);
          element.innerHTML = '';
        }
      });

      this.initialized = true; // Mark as initialized to prevent re-rendering
    } catch (error) {
      console.error('Error during initial cart icon render:', error);
    }
  }

  handleIntersection(entries, observer) {
    if (!entries[0].isIntersecting) return;
    observer.unobserve(this);

    fetch(this.dataset.url)
      .then(response => response.text())
      .then(html => {
        const miniCartElement = document.getElementById('mini-cart');
        if (miniCartElement) {
          miniCartElement.innerHTML = this.getSectionInnerHTML(html, '.shopify-section') || '';
          document.dispatchEvent(new CustomEvent('cartdrawer:opened'));
        } else {
          console.error('Mini-cart element not found');
        }
      })
      .catch(e => {
        console.error('Error loading mini-cart:', e);
      });
  }

  open() {
    pushViewCartEvent();
    const detailsElement = this.drawer?.querySelector('details');
    if (detailsElement && !detailsElement.hasAttribute('open')) {
      this.drawer.openMenuDrawer();
    }
  }

  // Compatibility shim for older calls to renderContents
  renderContents(parsedState) {
    // Redirect to updateContents with openDrawer set to true (for card-product)
    this.updateContents(parsedState, true);
  }

  async updateContents(parsedState, openDrawer = false) {
    if (!parsedState || !parsedState.sections) {
      console.error('Invalid parsedState:', parsedState);
      return;
    }

    this.productId = parsedState.id;
    const sectionsToRender = this.getSectionsToRender();

    // Check for missing sections and fetch them if necessary
    const missingSections = sectionsToRender
      .filter(section => !parsedState.sections[section.section])
      .map(section => section.section)
      .filter(Boolean);

    let updatedSections = { ...parsedState.sections };

    if (missingSections.length > 0) {
      console.log(`Fetching missing sections: ${missingSections.join(',')}`);
      try {
        const sectionResponse = await fetch(`?sections=${missingSections.join(',')}`);
        if (!sectionResponse.ok) throw new Error(`Failed to fetch missing sections: ${sectionResponse.status}`);
        const sectionData = await sectionResponse.json();
        updatedSections = { ...updatedSections, ...sectionData };
      } catch (error) {
        console.error('Error fetching missing sections:', error);
      }
    }

    sectionsToRender.forEach((section) => {
      const element = document.getElementById(section.id);
      if (!element) {
        console.warn(`Element with ID ${section.id} not found`);
        return;
      }
      const html = updatedSections[section.section];
      if (html) {
        const innerHTML = this.getSectionInnerHTML(html, section.selector);
        if (innerHTML) {
          element.innerHTML = innerHTML;
        } else {
          console.warn(`No valid HTML for section ${section.id}, preserving existing content`);
          // Preserve existing content if new content is invalid
        }
      } else {
        console.warn(`No section data for ${section.id} after fetch, preserving existing content`);
        // Preserve existing content if section data is still missing
      }
    });

    // Open the cart drawer if specified (e.g., for card-product Add to Cart)
    if (openDrawer) {
      this.open();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'mini-cart',
        section: 'mini-cart',
        selector: '.shopify-section'
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      },
      {
        id: 'mobile-cart-icon-bubble',
        section: 'mobile-cart-icon-bubble',
        selector: '.shopify-section'
      }
    ].filter(section => section.section); // Filter out undefined sections
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) {
      console.warn('No HTML provided for parsing');
      return '';
    }
    try {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const element = parsed.querySelector(selector);
      return element ? element.innerHTML : '';
    } catch (error) {
      console.error('Error parsing HTML:', error);
      return '';
    }
  }
}

customElements.define('mini-cart', MiniCart);
}
